import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  // Lock body scroll when open so background doesn't move on mobile.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex md:items-center md:justify-center items-end md:p-4">
      {/* Scrim */}
      <div className="absolute inset-0 bg-ink-900/50 animate-fade-in" onClick={onClose} aria-hidden />

      {/* Sheet (mobile) / Modal (desktop) */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`
          relative bg-white shadow-xl flex flex-col
          w-full max-h-[92vh] rounded-t-2xl animate-sheet-up
          md:rounded-xl md:max-h-[85vh] md:animate-fade-in md:w-full
          ${wide ? "md:max-w-2xl" : "md:max-w-md"}
        `}
      >
        {/* Drag handle (mobile only — visual cue, not interactive) */}
        <div className="md:hidden flex justify-center pt-2.5 pb-1">
          <div className="h-1 w-10 rounded-full bg-ink-200" />
        </div>

        {/* Header */}
        <div className="px-5 md:px-6 py-3 md:py-4 border-b border-ink-200 flex items-center justify-between gap-3">
          <h3 className="font-semibold text-base md:text-lg truncate">{title}</h3>
          <button
            className="text-ink-400 hover:text-ink-700 p-2 -mr-2"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — scrolls inside */}
        <div className="px-5 md:px-6 py-4 overflow-y-auto pb-safe md:pb-4">{children}</div>

        {footer && (
          <div className="px-5 md:px-6 py-3 md:py-4 border-t border-ink-200 flex justify-end gap-2 pb-safe md:pb-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
